import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewContainerRef,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronDown, lucideDownload, lucideHeart, lucideLogOut, lucideMoon, lucideSun } from '@ng-icons/lucide';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { ConfirmDialogComponent, HlmDialogOpener, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { IS_MOBILE } from '@lite-workspace/utils';

export interface StickyBarScene {
  id: string;
  title: string;
}

/** «Стиль меню» (spec «Галерея клиента» 5): icons alone, or each action with its word. */
export type StickyBarStyle = 'icon' | 'text';

type StickyBarAction = {
  key: 'scheme' | 'download' | 'favourites' | 'contactCard' | 'logout';
  label: string;
  icon?: string;
  /** The scheme switch stays a glyph in every bar style — its word is the aria-label. */
  iconOnly?: boolean;
  /**
   * «Скачать галерею» is the bar's ONE word — glyph plus word in every bar style, off the phone.
   * On the phone it is a glyph like its neighbours: the word would squeeze the folder strip to nothing.
   */
  alwaysLabelled?: boolean;
};

// «Визитка» has no lucide glyph that reads as a business card, so the gallery carries
// its own passport-card one (approved variant D): card outline, bust on the left, two
// lines on the right. Same attribute set as a lucide icon — `currentColor`, round
// caps/joins, the ng-icon stroke-width var — so it stands in one voice with them.
// One string for both places the visitka is offered: this bar and the footer button.
export const galleryContactCard =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-linecap="round" stroke-linejoin="round" style="stroke-width:var(--ng-icon__stroke-width, 2)">' +
  '<rect x="2" y="4" width="20" height="16" rx="2.5"></rect>' +
  '<circle cx="8.6" cy="10" r="2.3"></circle>' +
  '<path d="M5 16.4a3.6 3.6 0 0 1 7.2 0"></path>' +
  '<path d="M15.2 10.4h3.8"></path>' +
  '<path d="M15.2 14.2h2.8"></path></svg>';

// D8: the action glyphs read as the bar's second voice beside the small-caps
// tabs — 22 px, one number for the whole group, never a per-action size.
const ACTION_ICON_PX = '22';

// Spec item 6, first squeeze step of the «text» style: the words stay only while
// they leave room for the first tab and the «Ещё…» trigger inside the air budget.
export function stickyBarLabelsFit(availableWithLabels: number, firstTabWidth: number): boolean {
  return availableWithLabels >= firstTabWidth + MORE_TRIGGER_PX + Math.round(availableWithLabels * AIR_FRACTION);
}

// Width reserved for the «Ещё…» trigger (label + chevron + its own tab padding)
// — lab gallery-bar.tsx MORE_TRIGGER_PX.
const MORE_TRIGGER_PX = 88;
// Mandatory breathing zone scenes may never enter — a fraction of the strip
// width, so the reserved air scales with the window (lab AIR_FRACTION).
const AIR_FRACTION = 0.12;

// The public gallery's own tab look (spec «Галерея клиента» 44): small caps —
// uppercase 13 px / 500 / 0,08em — muted when idle, ink when active, with an ink
// underline. Deliberately NOT the cabinet's `hlmNavtab` (14,5 px semibold, accent
// underline, no caps): same role, a different typography by spec.
// The 16 px side padding IS the 32 px inter-tab air of spec item 6 — kept as
// padding, not a flex gap, so the measured tab width already carries it.
const TAB_CLASS =
  'relative inline-flex h-full shrink-0 cursor-pointer items-center px-4 text-control ' +
  'font-medium tracking-[var(--ls-caps-m)] whitespace-nowrap uppercase transition-colors duration-[var(--t-fast)] ' +
  'ease-[var(--ease-smooth)] hover:text-ink';

// D8: the bar's ONE height (56 px on the 8 px air scale) — a thin band, tabs and
// actions centred inside it. Every inner box is `h-full`, so this class is the
// single number desktop and phone (and the cabinet preview) all read.
export const STICKY_BAR_ROW_CLASS = 'flex h-14 w-full items-center gap-4 px-6 max-md:gap-2 max-md:px-3';

// A landscape phone is 844 px wide — past `md` — yet still a phone, so the
// strip's sideways scroll and its 16 px edge fade hang on the same IS_MOBILE
// signal the «Ещё…» width budget already reads, never on a width-keyed
// `max-md:` arm. One source of truth for «this is the phone strip».
const MOBILE_STRIP_CLASS =
  'overflow-x-auto [-webkit-overflow-scrolling:touch] ' +
  '[mask-image:linear-gradient(to_right,transparent,#000_16px,#000_calc(100%_-_16px),transparent)]';

@Component({
  selector: 'gallery-sticky-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmDropdownMenuImports, HlmButton, HlmTooltip, NgIcon, NgTemplateOutlet],
  providers: [
    provideIcons({
      galleryContactCard,
      lucideChevronDown,
      lucideDownload,
      lucideHeart,
      lucideLogOut,
      lucideMoon,
      lucideSun,
    }),
  ],
  // Translate-only hide keeps the bar in flow (sticky) and honours reduced motion.
  // Lab parity (gallery-bar.tsx): a clean SOLID band on the page surface — no
  // bottom border, no blur, no max-width; px-6 gutters + py-4 breathing.
  host: {
    class:
      'sticky top-0 z-10 block bg-[var(--gallery-bg)] pt-[var(--safe-top)] ' +
      'transition-transform duration-[var(--t-mid)] ease-[var(--ease-smooth)] ' +
      'motion-reduce:transition-none motion-reduce:translate-y-0',
    '[class.-translate-y-full]': '!visible()',
    '(mouseenter)': 'hovered.emit(true)',
    '(mouseleave)': 'hovered.emit(false)',
  },
  template: `
    <div #row [class]="rowClass">
      <!-- Phone (spec «Галерея клиента» 6, mobile law): no width budget, no «Ещё…» —
           the strip simply scrolls sideways, bar-free, and never eats the actions.
           The 16 px edge fade is unconditional on the phone and still honest: a tab
           carries 16 px of its own side padding, so at either scroll end (and when
           nothing overflows at all) the masked band covers empty padding and reads
           as nothing — it only bites where a word really continues past the edge. -->
      <!-- T4.1: one scene = nothing to switch between, so the whole strip (its
           hidden measuring layer included) leaves the DOM. The bar itself stays
           — it carries the download, favourites, visitka, scheme and logout
           actions — and they slide to the row's end in its place. -->
      @if (!hideTabs()) {
        <nav
          #strip
          class="no-scrollbar relative flex h-full min-w-0 flex-1 items-center"
          [class]="stripClass()"
          aria-label="Папки"
        >
          <!-- Hidden measuring layer: the FULL tab strip, absolute + invisible, read
               once per scene set to capture each tab's natural width (lab
               gallery-bar.tsx width-driven overflow). It carries the SAME tab look
               as the live strip — a width read off another look lies to the budget.
               The «text» style also measures its labelled actions here, so the
               label-dropping step can be decided without ever flashing them. -->
          @if (measuring() && !mobile()) {
            <div #measureLayer aria-hidden="true" class="pointer-events-none invisible absolute flex h-full items-center">
              @for (scene of scenes(); track scene.id) {
                <span data-tab [class]="tabClass">{{ scene.title }}</span>
              }
              @if (barStyle() === 'text') {
                <span #measureActions class="flex items-center gap-1">
                  @for (action of actions(); track action.key) {
                    <ng-container
                      [ngTemplateOutlet]="action.iconOnly ? glyph : labelled"
                      [ngTemplateOutletContext]="{ $implicit: action }"
                    />
                  }
                </span>
              }
            </div>
          }

          <!-- The phone strip only overflows while the tab row refuses to shrink. -->
          <div class="flex h-full min-w-0 items-center" [class.shrink-0]="mobile()">
            @for (scene of visibleScenes(); track scene.id) {
              <button
                type="button"
                [class]="tabClass"
                [class.text-ink]="scene.id === activeSceneId()"
                [class.text-c2]="scene.id !== activeSceneId()"
                [attr.aria-current]="scene.id === activeSceneId() ? 'true' : null"
                (click)="sceneSelect.emit(scene.id)"
              >
                {{ scene.title }}
                @if (scene.id === activeSceneId()) {
                  <span class="bg-ink absolute inset-x-4 bottom-0 h-0.5"></span>
                }
              </button>
            }
          </div>

          <!-- «Ещё…» — the scenes that did not fit, in a text dropdown (lab
               gallery-bar.tsx overflow; width-driven, never a fixed count). -->
          @if (overflowScenes().length > 0) {
            <button type="button" [class]="moreClass" [hlmDropdownMenuTrigger]="moreMenu" align="start" side="bottom">
              Ещё…
              <ng-icon name="lucideChevronDown" size="16" />
            </button>
            <!-- The menu PORTALS to document.body — outside the gallery's scoped
                 theme root — so the gallery theme is re-stamped on the panel itself
                 (the lab portal-escapes-the-theme rule). -->
            <ng-template #moreMenu>
              <div
                hlmDropdownMenu
                class="border-line bg-panel2 max-w-[280px] min-w-[180px] rounded-[var(--r)] border p-[6px]
                  shadow-[var(--shadow-3)]"
                [class.dark]="dark()"
              >
                @for (scene of overflowScenes(); track scene.id) {
                  <button hlmDropdownMenuItem class="text-c2 w-full cursor-pointer" (triggered)="sceneSelect.emit(scene.id)">
                    <span class="truncate">{{ scene.title }}</span>
                  </button>
                }
              </div>
            </ng-template>
          }
        </nav>
      }

      <!-- Actions, in the bar order Скачать · Избранное · Визитка · Тема · Выйти.
           Default bar style is icons (item 5), each carrying its own word as the DS
           tooltip; the «text» style prints the word beside the icon in the tabs'
           small caps and falls back to icons on a narrow window (item 6). «Скачать»,
           «Визитка» and «Выйти» appear only when they lead somewhere — the page
           hands their live conditions in. -->
      <!-- Phone: the 44 px hit boxes sit edge to edge — the glyphs still stand 26 px
           apart inside them — so every spare pixel goes to the scene strip. -->
      <!-- An action reacts like a scene tab: only the paint moves. The ghost variant
           already carries c2 → ink on the paint transition (t-fast), so just its
           hover panel slab is dropped. The important flag is what beats the
           variant's own utility, whose order in the sheet is not ours to pick. -->
      <!-- No strip to share the row with (T4.1) → the actions keep the bar's end. -->
      <div #actionsBox class="flex shrink-0 items-center gap-1 max-md:gap-0" [class.ml-auto]="hideTabs()">
        @for (action of actions(); track action.key) {
          <ng-container
            [ngTemplateOutlet]="(showLabels() || (action.alwaysLabelled && !mobile())) && !action.iconOnly ? labelled : glyph"
            [ngTemplateOutletContext]="{ $implicit: action }"
          />
        }
      </div>
    </div>

    <ng-template #glyph let-action>
      <button
        hlmBtn
        variant="ghost"
        size="icon-sm"
        type="button"
        class="pointer-coarse:min-h-11 pointer-coarse:min-w-11 hover:bg-transparent!"
        [hlmTooltip]="action.label"
        position="bottom"
        [attr.aria-label]="action.label"
        (click)="act(action.key)"
      >
        <ng-icon [name]="action.icon" [size]="iconPx" />
      </button>
    </ng-template>

    <ng-template #labelled let-action>
      <button
        hlmBtn
        variant="ghost"
        size="sm"
        type="button"
        class="pointer-coarse:min-h-11 hover:bg-transparent!"
        [attr.aria-label]="action.label"
        (click)="act(action.key)"
      >
        @if (action.icon) {
          <ng-icon [name]="action.icon" [size]="iconPx" />
        }
        <span class="text-control font-medium tracking-[var(--ls-caps-m)] uppercase">{{ action.label }}</span>
      </button>
    </ng-template>
  `,
})
export class GalleryStickyBarComponent {
  readonly scenes = input.required<StickyBarScene[]>();
  readonly activeSceneId = input<string | null>(null);
  readonly visible = input(true);
  // The gallery's «Тема» for the portaled overflow menu (it escapes the themed root).
  readonly dark = input(false);
  // Spec item 7 — «Скачать» only while the gallery has a downloadable photo scene,
  // «Выйти» only when there is somewhere to log out to (both roles, or a saved
  // guest word).
  readonly downloadAvailable = input(false);
  // Hearts off (showcase, or the photographer's toggle) → no «Избранное» either.
  readonly favouritesAvailable = input(true);
  /** The card window is offered only when the photographer turned it on and filled it. */
  readonly contactCardAvailable = input(false);
  readonly logoutAvailable = input(false);
  readonly barStyle = input<StickyBarStyle>('icon');
  // T4.1: the host's single-scene verdict (raw scene count) — tabs off, bar on.
  readonly hideTabs = input(false);
  readonly sceneSelect = output<string>();
  /** The visitor asked for the other colour scheme; the page owns the flip. */
  readonly schemeToggle = output<void>();
  readonly hovered = output<boolean>();
  readonly download = output<void>();
  readonly favourites = output<void>();
  readonly contactCard = output<void>();
  /** Fires only after the visitor confirmed «Да, выйти». */
  readonly logout = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogs = inject(HlmDialogOpener);
  private readonly vcr = inject(ViewContainerRef);
  // One breakpoint for the whole product (max-width: 767px); the template mirrors it with `max-md:`.
  protected readonly mobile = inject(IS_MOBILE);

  protected readonly rowClass = STICKY_BAR_ROW_CLASS;
  protected readonly iconPx = ACTION_ICON_PX;
  protected readonly tabClass = TAB_CLASS;
  protected readonly moreClass = `${TAB_CLASS} text-c2 gap-1 outline-none`;
  protected readonly stripClass = computed(() => (this.mobile() ? MOBILE_STRIP_CLASS : ''));
  private readonly row = viewChild.required<ElementRef<HTMLElement>>('row');
  // Absent while `hideTabs` holds — a single-scene bar has no strip at all.
  private readonly strip = viewChild<ElementRef<HTMLElement>>('strip');
  private readonly actionsBox = viewChild.required<ElementRef<HTMLElement>>('actionsBox');
  private readonly measureLayer = viewChild<ElementRef<HTMLElement>>('measureLayer');
  private readonly measureActions = viewChild<ElementRef<HTMLElement>>('measureActions');

  protected readonly measuring = signal(true);
  private stripResize?: ResizeObserver;
  private tabWidths: number[] = [];
  private labelledActionsWidth = 0;
  private readonly visibleCount = signal(Number.MAX_SAFE_INTEGER);
  private readonly labelsFit = signal(true);

  protected readonly visibleScenes = computed(() => this.scenes().slice(0, this.visibleCount()));
  protected readonly overflowScenes = computed(() => this.scenes().slice(this.visibleCount()));
  // No room for words on a phone — every action stays an icon with its word as the aria-label.
  protected readonly showLabels = computed(() => this.barStyle() === 'text' && this.labelsFit() && !this.mobile());

  protected readonly actions = computed<StickyBarAction[]>(() => [
    // «Скачать галерею» opens the group as the bar's one word — glyph on its left, no tooltip
    // (a phone draws it as a glyph with the tooltip, see `alwaysLabelled`).
    ...(this.downloadAvailable()
      ? [{ key: 'download' as const, label: 'Скачать галерею', icon: 'lucideDownload', alwaysLabelled: true }]
      : []),
    ...(this.favouritesAvailable() ? [{ key: 'favourites' as const, label: 'Избранное', icon: 'lucideHeart' }] : []),
    ...(this.contactCardAvailable() ? [{ key: 'contactCard' as const, label: 'Визитка', icon: 'galleryContactCard' }] : []),
    // Always there, whatever the photographer's «Тема»: the glyph offers the
    // scheme the visitor would GET by clicking it. Second to last, before «Выйти».
    this.dark()
      ? { key: 'scheme' as const, label: 'Светлая тема', icon: 'lucideSun', iconOnly: true }
      : { key: 'scheme' as const, label: 'Тёмная тема', icon: 'lucideMoon', iconOnly: true },
    ...(this.logoutAvailable() ? [{ key: 'logout' as const, label: 'Выйти', icon: 'lucideLogOut' }] : []),
  ]);

  constructor() {
    this.destroyRef.onDestroy(() => this.stripResize?.disconnect());

    // Re-enter measuring when the scene SET, the action set, the bar style or the
    // breakpoint changes — widths must be re-read (a phone strip has none at all).
    effect(() => {
      this.scenes();
      this.actions();
      this.barStyle();
      this.mobile();
      this.hideTabs();
      untracked(() => {
        this.tabWidths = [];
        this.measuring.set(true);
        this.visibleCount.set(Number.MAX_SAFE_INTEGER);
      });
    });

    // Read the natural tab widths off the hidden layer once it is laid out,
    // drop the layer, compute the fit.
    afterRenderEffect(() => {
      if (!this.measuring()) return;
      const layer = this.measureLayer()?.nativeElement;
      if (!layer) return;
      const spans = layer.querySelectorAll<HTMLElement>('[data-tab]');
      if (spans.length !== untracked(() => this.scenes()).length) return;
      this.tabWidths = Array.from(spans).map(el => el.offsetWidth);
      this.labelledActionsWidth = this.measureActions()?.nativeElement.offsetWidth ?? 0;
      this.measuring.set(false);
      this.recompute();
    });

    // The phone strip scrolls, so the active scene has to bring itself into view.
    // Only the STRIP may move: `scrollIntoView` walks the whole ancestor chain up
    // to the document, and the bar sits a screen below the cover top — on the
    // first paint it dragged the page off the cover before the visitor touched
    // anything. Centring by `scrollLeft` touches one axis of one box.
    afterRenderEffect(() => {
      const active = this.activeSceneId();
      const strip = this.strip()?.nativeElement;
      if (!active || !this.mobile() || !strip) return;
      const tab = strip.querySelector<HTMLElement>('button[aria-current="true"]');
      if (!tab) return;
      strip.scrollLeft = tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2;
    });

    // The strip can leave and re-enter the DOM with `hideTabs`, so the width
    // watch follows whichever strip is on screen now (re-observing the same
    // element is a no-op). Browser-only, like every after-render hook.
    afterRenderEffect(() => {
      const strip = this.strip()?.nativeElement;
      if (!strip) return;
      this.stripResize ??= new ResizeObserver(() => this.recompute());
      this.stripResize.observe(strip);
    });
  }

  protected act(key: StickyBarAction['key']): void {
    if (key === 'scheme') this.schemeToggle.emit();
    else if (key === 'download') this.download.emit();
    else if (key === 'favourites') this.favourites.emit();
    else if (key === 'contactCard') this.contactCard.emit();
    else this.askLogout();
  }

  // Spec item 8: leaving is a decision, so it passes the shared confirm window
  // first; the page only hears the confirmed exit.
  protected askLogout(): void {
    const ref = this.dialogs.open<ConfirmDialogContext, boolean>(
      ConfirmDialogComponent,
      this.vcr,
      { title: 'Вы уверены, что хотите выйти?', cancelLabel: 'Нет, остаться', confirmLabel: 'Да, выйти' },
      { backCloses: true },
    );
    ref.closed$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed) this.logout.emit();
    });
  }

  // Lab fit budget: greedily keep tabs while each leaves the mandatory air free;
  // if not all fit, additionally reserve the «Ещё…» trigger and refit (min 1).
  private recompute(): void {
    const count = this.scenes().length;
    const widths = this.tabWidths;
    const strip = this.strip()?.nativeElement;
    if (widths.length !== count || count === 0 || !strip) return;
    const available = strip.clientWidth;
    if (available <= 0) {
      this.visibleCount.set(count);
      return;
    }

    // Item 6, first step of the «text» squeeze: decided on the row's width (which
    // the labels themselves never change), so dropping them cannot re-fit them.
    if (this.barStyle() === 'text') {
      const chrome = this.row().nativeElement.clientWidth - available - this.actionsBox().nativeElement.offsetWidth;
      const withLabels = this.row().nativeElement.clientWidth - chrome - this.labelledActionsWidth;
      this.labelsFit.set(stickyBarLabelsFit(withLabels, widths[0]));
    }

    const minAir = Math.round(available * AIR_FRACTION);

    const fit = (reserve: number): number => {
      let used = 0;
      let n = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const next = used + widths[i];
        if (next + reserve > available) break;
        used = next;
        n += 1;
      }

      return n;
    };

    let n = fit(minAir);
    if (n < count) {
      n = Math.max(1, fit(minAir + MORE_TRIGGER_PX));
    }

    this.visibleCount.set(n);
  }
}
